import type {
  AgentProvider,
  AgentSessionSegment,
  AgentVisibility,
  AgentSession as ApiAgentSession,
} from 'api/agentSessions/agentSessions.api';
import type { ProjectSuggestion } from 'api/agentSessions/projectSuggestions.schema';

export type { AgentProvider, AgentVisibility };

import { DateTime } from 'luxon';
import { hasDisplayableProject } from './projectSuggestionChoices';

/** Claude Code logs bookkeeping records under this pseudo-model; nothing was called. */
const PSEUDO_MODELS = new Set(['<synthetic>']);

/**
 * One activity period of an agent conversation, flattened for the Agent Tracking
 * views. The API nests periods under their session; the page mostly works per
 * day, so every period carries what it needs from its parent session.
 */
export interface AgentPeriod {
  id: string;
  /** Backend ids, needed to assign a project to this period or its session. */
  sessionId: number;
  /** Owner reported by the backend, not necessarily the signed-in viewer. */
  userId: number;
  segmentId: number;
  /** Groups the periods of one conversation: `${provider}:${provider_session_id}`. */
  sessionKey: string;
  /** Who besides the owner sees the owning session; every period of a session shares it. */
  sessionVisibility: AgentVisibility;
  /** Local calendar day of the period (ISO date), as stored by the backend. */
  date: string;
  /** Wall-clock start/end, HH:mm, in the offset the period was recorded in (see toAgentPeriods). */
  start: string;
  end: string;
  startedAt: string;
  /** ISO end of the period; absent on periods built before it was kept. */
  endedAt?: string;
  activeSeconds: number;
  metricsAvailable?: boolean;
  metricAvailability?: Record<string, boolean>;
  title: string;
  provider: AgentProvider;
  providerName: string;
  summary: string | null;
  projectId: number | null;
  defaultProjectId?: number | null;
  projectAssignmentSource?: 'manual' | 'inherited' | 'automatic' | null;
  projectSuggestion?: ProjectSuggestion | null;
  projectPredictionState?: string | null;
  labelIds?: number[];
  resourceId?: string | null;
  /**
   * Whether the viewer may change this period's project, tags and linked work (Rails'
   * can_edit): the owner always, someone who may edit the owner's hours on what Rails
   * allows them (TT-6346). Absent from older Rails, where only the owner could.
   */
  canEdit?: boolean;
  /** Whether the viewer may rename the owning session; shared by its periods. */
  sessionCanEdit?: boolean;
  /** A colleague's period: its project's name and color, which the viewer may not have loaded. */
  projectName?: string | null;
  projectColor?: string | null;
  inputTokens: number;
  outputTokens: number;
  /** Prompt-cache traffic; most of the volume of a long session, priced separately. */
  cacheReadTokens: number;
  cacheWriteTokens: number;
  /** Per-model counters as uploaded: tokens plus optional request/fast/effort counts. */
  tokenUsage: TokenUsage;
  speedUnknownUsage?: TokenUsage | null;
  speedOverrides?: Record<string, 'standard' | 'priority' | 'ultrafast'>;
  costAvailable?: boolean;
  /** Rails' standard API list-price estimate, fixed when the period was uploaded; not a bill. */
  cost: number;
  /** Rate card that priced the period; absent when it has no cost. */
  pricingVersion?: string | null;
  /**
   * Frontend only: set when a refresh while the page was open brought a change
   * (a new period, new telemetry or project state, or its summary), so the row
   * can highlight it. Cleared again shortly after.
   */
  liveChange?: { kind: 'period' | 'summary'; at: number };
  /**
   * Frontend only: the pill a live change leaves on its session for a while,
   * "new" when the session first appeared that way, else "updated".
   */
  liveBadge?: { kind: 'new' | 'updated'; until: number };
}

/** How long a session keeps its New or Updated pill after a live change. */
export const LIVE_BADGE_MS = 60_000;

export type TokenUsage = Record<string, Record<string, number>>;

export function sessionTitle(session: Pick<ApiAgentSession, 'title' | 'folder_name'>) {
  return session.title?.trim() || session.folder_name?.trim() || 'Untitled session';
}

/** Keep source placeholders recoverable: suppress them only in the activity view.
 * Unknown metrics are not evidence of zero activity, and user edits must stay visible.
 */
function isEmptyAgentSegment(segment: AgentSessionSegment, session: ApiAgentSession): boolean {
  const counters = [
    segment.active_seconds,
    segment.input_tokens,
    segment.output_tokens,
    segment.cache_read_tokens,
    segment.cache_write_tokens,
    segment.total_tokens,
    ...Object.values(segment.tool_counts),
    ...Object.values(segment.token_usage).flatMap(Object.values),
    ...Object.values(segment.speed_unknown_usage ?? {}).flatMap(Object.values),
  ];
  return (
    session.metrics_available !== false &&
    !Object.values(segment.metric_availability ?? {}).includes(false) &&
    counters.every(value => value === 0) &&
    (segment.estimated_cost == null || segment.estimated_cost === 0) &&
    !segment.summary?.trim() &&
    segment.project_id === null &&
    segment.project_assignment_source !== 'manual' &&
    !segment.project_suggestion &&
    !segment.label_ids?.length &&
    !segment.resource_id &&
    !Object.keys(segment.speed_overrides ?? {}).length
  );
}

/**
 * Flattens the API response into one period per segment, oldest first.
 *
 * Times keep the offset they arrive with (`setZone`) instead of being moved
 * to the browser's zone: `day` is the local calendar day the period was
 * recorded on, and the backend serves timestamps in the user's Timely time
 * zone, so a period at 00:15+02:00 must read 00:15 under its day, not 22:15
 * on the day before for someone whose browser happens to be on UTC.
 */
export function toAgentPeriods(sessions: ApiAgentSession[]): AgentPeriod[] {
  return (
    sessions
      .flatMap(session =>
        session.segments
          .filter(segment => !isEmptyAgentSegment(segment, session))
          .map((segment): AgentPeriod => {
            const startedAt = DateTime.fromISO(segment.started_at, { setZone: true });
            const endedAt = DateTime.fromISO(segment.ended_at, { setZone: true });
            const estimate = segment.estimated_cost ?? null;
            return {
              id: `${session.id}:${segment.segment_key}`,
              sessionId: session.id,
              userId: session.user_id,
              segmentId: segment.id,
              // Per person: on a board of the whole workspace, two people's sessions may share a
              // provider's session id (synced machines, a copied upload) and must stay two rows.
              sessionKey: `${session.user_id}:${session.provider}:${session.provider_session_id}`,
              sessionVisibility: session.visibility,
              date: segment.day,
              start: startedAt.toFormat('HH:mm'),
              end: endedAt.toFormat('HH:mm'),
              startedAt: segment.started_at,
              endedAt: segment.ended_at,
              activeSeconds: segment.active_seconds,
              metricsAvailable: session.metrics_available,
              metricAvailability: segment.metric_availability,
              title: sessionTitle(session),
              provider: session.provider,
              providerName: session.provider_name,
              summary: segment.summary,
              projectId: segment.project_id,
              defaultProjectId: session.default_project_id,
              projectAssignmentSource: segment.project_assignment_source,
              projectSuggestion: segment.project_suggestion,
              projectPredictionState: segment.project_prediction_state,
              labelIds: segment.label_ids ?? [],
              resourceId: segment.resource_id ?? null,
              canEdit: segment.can_edit,
              sessionCanEdit: session.can_edit,
              projectName: segment.project_name,
              projectColor: segment.project_color,
              inputTokens: segment.input_tokens,
              outputTokens: segment.output_tokens,
              cacheReadTokens: segment.cache_read_tokens,
              cacheWriteTokens: segment.cache_write_tokens,
              tokenUsage: segment.token_usage,
              speedOverrides: segment.speed_overrides,
              speedUnknownUsage: segment.speed_unknown_usage,
              cost: estimate ?? 0,
              // Rails leaves the estimate empty for unknown models or tiers, unmeasured
              // tokens and viewers who may not see cost.
              costAvailable: estimate !== null,
              pricingVersion: segment.pricing_version ?? null,
            };
          })
      )
      // ISO timestamps order as plain strings; localeCompare costs many times more on a day of
      // thousands of periods.
      .sort((a, b) => (a.startedAt < b.startedAt ? -1 : a.startedAt > b.startedAt ? 1 : 0))
  );
}

export interface AgentTotals {
  seconds: number;
  inputTokens: number;
  outputTokens: number;
  /** All token traffic: uncached input, output, cache reads and cache writes. */
  tokens: number;
  cacheTokens: number;
  cost: number;
}

export function hasAgentMetric(
  period: AgentPeriod,
  metric:
    | 'seconds'
    | 'tokens'
    | 'cost'
    | 'input_tokens'
    | 'output_tokens'
    | 'cache_read_tokens'
    | 'cache_write_tokens'
): boolean {
  const available = (field: string) =>
    period.metricAvailability?.[field] ?? period.metricsAvailable !== false;
  if (metric === 'seconds') return available('active_seconds');
  if (metric === 'tokens')
    return ['input_tokens', 'output_tokens', 'cache_read_tokens', 'cache_write_tokens'].every(
      available
    );
  if (metric === 'cost')
    return (
      period.costAvailable !== false &&
      ['input_tokens', 'output_tokens', 'cache_read_tokens', 'cache_write_tokens'].every(available)
    );
  return available(metric);
}

/** Providers the Memory app uploads and writes summaries for; others never get one. */
const SUMMARIZED_PROVIDERS = new Set<AgentProvider>(['claude_code', 'codex']);

/**
 * How long after its last activity a period without a summary still counts as
 * waiting for one when there is no server-confirmed waiting state. Otherwise, the Memory app writes
 * it once the agent has been quiet for its idle split (15 minutes by default)
 * and uploads it a minute or two later, but it never tells Rails whether
 * summaries are switched off, and a closed laptop or a backlog delays them.
 * TODO: revisit this window, or replace it with an explicit per-period summary
 * state from the Memory app once Rails stores one.
 */
export const SUMMARY_PENDING_WINDOW_MS = 2 * 60 * 60 * 1000;

export type SummaryStatus = 'ready' | 'pending' | 'none';

export function summaryStatus(
  period: Pick<AgentPeriod, 'summary' | 'provider' | 'endedAt' | 'projectPredictionState'>,
  now: number = Date.now()
): SummaryStatus {
  if (period.summary?.trim()) return 'ready';
  if (period.projectPredictionState === 'waiting_summary') return 'pending';
  if (!period.endedAt || !SUMMARIZED_PROVIDERS.has(period.provider)) return 'none';
  const endedAt = DateTime.fromISO(period.endedAt).toMillis();
  return now - endedAt < SUMMARY_PENDING_WINDOW_MS ? 'pending' : 'none';
}

/** When a pending summary stops being expected, or null when it is not pending. */
export function summaryPendingEndsAt(
  period: Pick<AgentPeriod, 'summary' | 'provider' | 'endedAt' | 'projectPredictionState'>,
  now: number = Date.now()
): number | null {
  if (period.projectPredictionState === 'waiting_summary') return null;
  if (summaryStatus(period, now) !== 'pending' || !period.endedAt) return null;
  return DateTime.fromISO(period.endedAt).toMillis() + SUMMARY_PENDING_WINDOW_MS;
}

/** What a refresh can change about a period that someone would notice in the table. */
const LIVE_FIELDS = [
  'endedAt',
  'activeSeconds',
  'inputTokens',
  'outputTokens',
  'cacheReadTokens',
  'cacheWriteTokens',
  'cost',
  'projectId',
  'projectPredictionState',
] as const satisfies readonly (keyof AgentPeriod)[];

/**
 * Marks the periods of `next` that a refresh added or changed compared with
 * `previous`, keeping marks a still-running highlight has, and keeps the
 * `previous` object of every period whose data did not change. A summary that
 * arrived or changed is its own kind, so its text can fade in.
 */
export function markLiveChanges(
  previous: AgentPeriod[],
  next: AgentPeriod[],
  at: number = Date.now()
): AgentPeriod[] {
  const before = new Map(previous.map(period => [period.id, period]));
  const knownSessions = new Set(previous.map(period => period.sessionId));
  const until = at + LIVE_BADGE_MS;
  return next.map(period => {
    const old = before.get(period.id);
    const kept = {
      ...(old?.liveChange && { liveChange: old.liveChange }),
      ...(old?.liveBadge && old.liveBadge.until > at && { liveBadge: old.liveBadge }),
    };
    const changed = (kind: 'period' | 'summary', badge: 'new' | 'updated') => ({
      ...period,
      liveChange: { kind, at },
      // A session that arrived during this page's life stays "new" for its whole minute.
      liveBadge: { kind: kept.liveBadge?.kind === 'new' ? 'new' : badge, until },
    });
    if (!old) return changed('period', knownSessions.has(period.sessionId) ? 'updated' : 'new');
    if (period.summary && period.summary !== old.summary) return changed('summary', 'updated');
    if (LIVE_FIELDS.some(field => period[field] !== old[field]))
      return changed('period', 'updated');
    // Nothing changed: keep the loaded object, so memoized rows skip rendering it again.
    if (samePeriodData(old, period)) return old;
    return kept.liveChange || kept.liveBadge ? { ...period, ...kept } : period;
  });
}

/** Whether two periods hold the same data, ignoring the frontend-only live marks. */
function samePeriodData(a: AgentPeriod, b: AgentPeriod) {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  keys.delete('liveChange');
  keys.delete('liveBadge');
  for (const key of keys) {
    const x = a[key as keyof AgentPeriod];
    const y = b[key as keyof AgentPeriod];
    if (x === y) continue;
    // Token usage, metric availability, labels and suggestions are small plain JSON.
    if (typeof x !== 'object' || typeof y !== 'object' || x === null || y === null) return false;
    if (JSON.stringify(x) !== JSON.stringify(y)) return false;
  }
  return true;
}

/** Drops the pills that ran out by `now`. */
export function clearLiveBadges(periods: AgentPeriod[], now: number): AgentPeriod[] {
  if (!periods.some(period => period.liveBadge && period.liveBadge.until <= now)) return periods;
  return periods.map(period => {
    if (!period.liveBadge || period.liveBadge.until > now) return period;
    const { liveBadge: _, ...rest } = period;
    return rest;
  });
}

/** The pill a session shows: "new" when any of its periods is new, else "updated", if any. */
export function sessionLiveBadge(periods: AgentPeriod[]): 'new' | 'updated' | null {
  const kinds = periods.flatMap(period => (period.liveBadge ? [period.liveBadge.kind] : []));
  if (kinds.includes('new')) return 'new';
  return kinds.length ? 'updated' : null;
}

/** Drops the marks made at or before `at`, so a newer highlight keeps running. */
export function clearLiveChanges(periods: AgentPeriod[], at: number): AgentPeriod[] {
  if (!periods.some(period => period.liveChange && period.liveChange.at <= at)) return periods;
  return periods.map(period => {
    if (!period.liveChange || period.liveChange.at > at) return period;
    const { liveChange: _, ...rest } = period;
    return rest;
  });
}

/** Tier counters and cache-write lifetime splits are subsets, not additional tokens. */
export function totalAgentTokens(period: AgentPeriod): number {
  return (
    period.inputTokens + period.outputTokens + period.cacheReadTokens + period.cacheWriteTokens
  );
}

export function summarizePeriods(periods: AgentPeriod[]): AgentTotals {
  return periods.reduce(
    (total, period) => ({
      seconds: total.seconds + (hasAgentMetric(period, 'seconds') ? period.activeSeconds : 0),
      inputTokens:
        total.inputTokens + (hasAgentMetric(period, 'input_tokens') ? period.inputTokens : 0),
      outputTokens:
        total.outputTokens + (hasAgentMetric(period, 'output_tokens') ? period.outputTokens : 0),
      tokens: total.tokens + (hasAgentMetric(period, 'tokens') ? totalAgentTokens(period) : 0),
      cacheTokens:
        total.cacheTokens +
        (hasAgentMetric(period, 'cache_read_tokens') ? period.cacheReadTokens : 0) +
        (hasAgentMetric(period, 'cache_write_tokens') ? period.cacheWriteTokens : 0),
      cost: total.cost + (hasAgentMetric(period, 'cost') ? period.cost : 0),
    }),
    { seconds: 0, inputTokens: 0, outputTokens: 0, tokens: 0, cacheTokens: 0, cost: 0 }
  );
}

/**
 * Estimated cost for a set of periods. Periods without an estimate simply don't contribute,
 * so a total only reads "Unknown" when nothing in it could be priced.
 */
export function formatEstimatedCost(periods: AgentPeriod[], value: number) {
  const unknown = periods.filter(period => period.costAvailable === false).length;
  if (unknown === periods.length && periods.length > 0) return 'Unknown';
  return formatCost(value);
}

/** Unknown measurements never masquerade as zero, including mixed-provider totals. */
export function formatKnownMetric(
  periods: AgentPeriod[],
  value: number,
  format: (value: number) => string
) {
  const available = (period: AgentPeriod) =>
    hasAgentMetric(
      period,
      format === formatCost ? 'cost' : format === formatDuration ? 'seconds' : 'tokens'
    );
  const unknown = periods.some(period => !available(period));
  if (!unknown) return format(value);
  return periods.some(available) ? `${format(value)} + unknown` : 'Unknown';
}

/** Seconds in, rounded to whole minutes once, here, for display. */
export function formatDuration(seconds: number) {
  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  return hours ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
}
export const formatTokens = (tokens: number) =>
  new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 2 }).format(tokens);
export const formatCost = (cost: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cost);

/** A conversation may have several periods, including across days. */
export function countSessions(periods: AgentPeriod[]) {
  return new Set(periods.map(period => period.sessionKey)).size;
}

export interface ProjectLabel {
  name: string;
  color: string | null;
  subHeading?: string;
}
export type ProjectLookup = (projectId: number | null) => ProjectLabel;

export const UNASSIGNED_PROJECT: ProjectLabel = { name: 'Unassigned', color: null };

export type SessionGrouping = 'day' | 'session' | 'provider' | 'project';

export interface SessionGroup {
  key: string;
  title: string;
  periods: AgentPeriod[];
}

/** Partition displayed periods exactly once; sessions may span days or projects. */
export function groupSessionPeriods(
  periods: AgentPeriod[],
  grouping: SessionGrouping,
  projectLookup: ProjectLookup
): SessionGroup[] {
  if (!periods.length) return [];
  if (grouping === 'session') return [{ key: 'sessions', title: '', periods }];
  const groups = new Map<string, SessionGroup>();
  for (const period of periods) {
    const key =
      grouping === 'day'
        ? period.date
        : grouping === 'provider'
          ? period.provider
          : String(period.projectId ?? 'unassigned');
    const title =
      grouping === 'day'
        ? DateTime.fromISO(period.date).toFormat('cccc, MMMM d')
        : grouping === 'provider'
          ? period.providerName
          : projectLookup(period.projectId).name;
    const group = groups.get(key);
    if (group) group.periods.push(period);
    else groups.set(key, { key, title, periods: [period] });
  }
  return [...groups.values()].sort((a, b) =>
    grouping === 'day'
      ? b.key.localeCompare(a.key)
      : a.title.localeCompare(b.title) || a.key.localeCompare(b.key)
  );
}

export type AgentProjectStatus = 'all' | 'unassigned' | 'suggested' | 'assigned';
export const PROJECT_STATUS_OPTIONS: { value: AgentProjectStatus; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'suggested', label: 'Suggested' },
  { value: 'assigned', label: 'Assigned' },
];

export interface AgentSessionFilters {
  query: string;
  projectStatus?: AgentProjectStatus;
  provider: string;
  project: string;
}
export const EMPTY_SESSION_FILTERS: AgentSessionFilters = { query: '', provider: '', project: '' };
export type SessionSortKey = 'activity' | 'title' | 'seconds' | 'tokens' | 'cost';
export interface SessionSort {
  key: SessionSortKey;
  direction: 'asc' | 'desc';
}
export const DEFAULT_SESSION_SORT: SessionSort = { key: 'activity', direction: 'desc' };

export function filterAgentPeriods(
  periods: AgentPeriod[],
  filters: AgentSessionFilters
): AgentPeriod[] {
  const query = filters.query.trim().toLocaleLowerCase();
  return periods.filter(
    period =>
      (!query || period.title.toLocaleLowerCase().includes(query)) &&
      (!filters.provider || period.provider === filters.provider) &&
      (!filters.project || String(period.projectId ?? 'unassigned') === filters.project) &&
      matchesProjectStatus(period, filters.projectStatus ?? 'all')
  );
}

export function matchesProjectStatus(period: AgentPeriod, status: AgentProjectStatus): boolean {
  switch (status) {
    case 'all':
      return true;
    case 'assigned':
      return period.projectId !== null;
    case 'unassigned':
      return period.projectId === null;
    case 'suggested':
      return (
        period.projectId === null &&
        !!period.projectSuggestion &&
        hasDisplayableProject(period.projectSuggestion)
      );
  }
}

/** Counts activity periods within the other filters, before the status selection. */
export function projectStatusCounts(periods: AgentPeriod[], filters: AgentSessionFilters) {
  const matching = filterAgentPeriods(periods, { ...filters, projectStatus: 'all' });
  return Object.fromEntries(
    PROJECT_STATUS_OPTIONS.map(({ value }) => [
      value,
      matching.filter(period => matchesProjectStatus(period, value)).length,
    ])
  ) as Record<AgentProjectStatus, number>;
}

/** Unknown measurements stay last in either direction; ties use newest period then session ID. */
export function sortTrackedSessions(
  sessions: TrackedSession[],
  sort: SessionSort
): TrackedSession[] {
  const value = (session: TrackedSession): number | string | null => {
    if (sort.key === 'activity') return session.latestActivity;
    if (sort.key === 'title') return session.title.toLocaleLowerCase();
    if (
      session.periods.some(
        period => !hasAgentMetric(period, sort.key as 'seconds' | 'tokens' | 'cost')
      )
    )
      return null;
    return session.totals[sort.key];
  };
  return [...sessions].sort((a, b) => {
    const left = value(a);
    const right = value(b);
    if (left === null && right !== null) return 1;
    if (right === null && left !== null) return -1;
    const compared =
      left === null || right === null
        ? 0
        : typeof left === 'string' && typeof right === 'string'
          ? left.localeCompare(right)
          : Number(left) - Number(right);
    return (
      compared * (sort.direction === 'asc' ? 1 : -1) ||
      b.latestActivity - a.latestActivity ||
      a.sessionKey.localeCompare(b.sessionKey)
    );
  });
}

export interface TrackedSession {
  sessionKey: string;
  title: string;
  visibility: AgentVisibility;
  periods: AgentPeriod[];
  projectIds: Array<number | null>;
  totals: AgentTotals;
  /**
   * When the session last did something (ms): a long session still working sorts above
   * one that started later but already stopped, and rises as live updates extend it.
   * Computed once here, not per comparison, so sorting thousands of sessions stays cheap.
   */
  latestActivity: number;
}

/**
 * Whether the viewer may change this period's project, tags and linked work (TT-6346):
 * Rails' can_edit, or for an older Rails that leaves it out, whether the viewer owns it.
 */
export function canEditPeriod(period: AgentPeriod, viewerId: number): boolean {
  return period.canEdit ?? period.userId === viewerId;
}

/** A session row is editable when every period of it is; mixed rows stay read-only. */
export function canEditSession(session: Pick<TrackedSession, 'periods'>, viewerId: number) {
  return (
    session.periods.length > 0 && session.periods.every(period => canEditPeriod(period, viewerId))
  );
}

/** Publishing, deleting and the owner's settings stay with the owner. */
export function ownsSession(session: Pick<TrackedSession, 'periods'>, viewerId: number) {
  return session.periods[0]?.userId === viewerId;
}

/** One row per conversation, in order of first activity, independent of provider. */
export function groupPeriodsBySession(periods: AgentPeriod[]): TrackedSession[] {
  const bySession = new Map<string, AgentPeriod[]>();
  for (const period of periods) {
    const sessionPeriods = bySession.get(period.sessionKey);
    if (sessionPeriods) sessionPeriods.push(period);
    else bySession.set(period.sessionKey, [period]);
  }
  return [...bySession.entries()].map(([sessionKey, sessionPeriods]) => ({
    sessionKey,
    title: sessionPeriods[0].title,
    visibility: sessionPeriods[0].sessionVisibility,
    periods: sessionPeriods,
    projectIds: [...new Set(sessionPeriods.map(period => period.projectId))],
    totals: summarizePeriods(sessionPeriods),
    latestActivity: Math.max(
      ...sessionPeriods.map(period => Date.parse(period.endedAt ?? period.startedAt))
    ),
  }));
}

/** Reasoning-effort levels the providers log, lowest first; the API sends one `effort_<level>` counter each. */
const EFFORT_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max', 'ultra'] as const;
type EffortLevel = (typeof EFFORT_LEVELS)[number];

/**
 * Pricing tiers other than standard: Claude's fast mode, and the Codex service
 * tiers. The API sends the tokens that ran in each as `<tier>_input|output|
 * cache_read|cache_write`, parts of the model's totals; standard is what they leave.
 */
const EXTRA_TIERS = ['ultrafast', 'priority', 'fast', 'flex'] as const;
const TOKEN_COUNTERS = ['input', 'output', 'cache_read', 'cache_write'] as const;
type ExtraTier = (typeof EXTRA_TIERS)[number];
type ServiceTier = ExtraTier | 'standard';

/**
 * Fastest first. Standard sits above flex, which is cheaper and slower than it,
 * so a session is badged flex only when nothing ran faster, standard included.
 */
const TIERS_BY_SPEED: readonly ServiceTier[] = [
  'ultrafast',
  'priority',
  'fast',
  'standard',
  'flex',
];

const TIER_LABELS: Record<ServiceTier, string> = {
  ultrafast: 'Ultrafast mode',
  priority: 'Fast mode',
  fast: 'Fast',
  standard: 'Standard',
  flex: 'Flex',
};

const tokenCounters = (counts: Record<string, number>, prefix = '') =>
  TOKEN_COUNTERS.reduce((sum, counter) => sum + (counts[`${prefix}${counter}`] ?? 0), 0);

export interface ModelUsage {
  model: string;
  /** Short display name, e.g. "Opus 5.5", "GPT-6 Astra". */
  label: string;
  /** All token traffic attributed to this model, including cache reads and writes. */
  tokens: number;
  requests: number;
  fastRequests: number;
  /** Of `tokens`, those that ran in each tier other than standard. */
  tierTokens: Record<ExtraTier, number>;
  effort: Record<EffortLevel, number>;
}

/**
 * "claude-opus-5-5" → "Opus 5.5", "claude-fable-5-1" → "Fable 5.1",
 * "gpt-6-astra" → "GPT-6 Astra", "gemini-3-pro" → "Gemini 3 Pro". Unknown
 * shapes fall back to the raw id.
 */
export function formatModelName(model: string) {
  const claude = model.match(/^claude-([a-z]+)-(\d+)(?:-(\d+))?/);
  if (claude) {
    const [, family, major, minor] = claude;
    return `${capitalize(family)} ${major}${minor ? `.${minor}` : ''}`;
  }
  const gpt = model.match(/^gpt-(\d+(?:\.\d+)?)(?:-([a-z]+))?/);
  if (gpt) return `GPT-${gpt[1]}${gpt[2] ? ` ${capitalize(gpt[2])}` : ''}`;
  const gemini = model.match(/^gemini-(\d+(?:\.\d+)?)(?:-([a-z]+))?/);
  if (gemini) return `Gemini ${gemini[1]}${gemini[2] ? ` ${capitalize(gemini[2])}` : ''}`;
  return model;
}

const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

/** Per-model usage summed over the given periods, biggest token share first. */
export function modelUsage(periods: AgentPeriod[]): ModelUsage[] {
  const byModel = new Map<string, ModelUsage>();
  for (const period of periods) {
    for (const [model, counts] of Object.entries(period.tokenUsage)) {
      if (PSEUDO_MODELS.has(model)) continue;
      const usage = byModel.get(model) ?? {
        model,
        label: formatModelName(model),
        tokens: 0,
        requests: 0,
        fastRequests: 0,
        tierTokens: { ultrafast: 0, priority: 0, fast: 0, flex: 0 },
        effort: { low: 0, medium: 0, high: 0, xhigh: 0, max: 0, ultra: 0 },
      };
      usage.tokens += tokenCounters(counts);
      usage.requests += counts.requests ?? 0;
      usage.fastRequests += counts.fast_requests ?? 0;
      for (const tier of EXTRA_TIERS) usage.tierTokens[tier] += tokenCounters(counts, `${tier}_`);
      for (const level of EFFORT_LEVELS) usage.effort[level] += counts[`effort_${level}`] ?? 0;
      byModel.set(model, usage);
    }
  }
  return [...byModel.values()].sort((a, b) => b.tokens - a.tokens);
}

/**
 * Speed of the model's requests: Claude Code's fast mode (`usage.speed ==
 * "fast"`, billed higher) versus standard. Always stated when the provider
 * reports request counts, so "no fast mode" is visible rather than implied.
 * Codex reports tokens per service tier rather than requests, so its split is
 * by token share.
 */
export function describeSpeed(usage: ModelUsage) {
  if (!usage.requests) return '';
  if (EXTRA_TIERS.some(tier => tier !== 'fast' && usage.tierTokens[tier] > 0)) {
    return tierShares(tokensByTier([usage]))
      .map(({ tier, share }) => `${TIER_LABELS[tier].toLowerCase()} ${formatShare(share)}`)
      .join(' / ');
  }
  if (usage.fastRequests === 0) return 'standard';
  if (usage.fastRequests >= usage.requests) return 'fast';
  return `fast ${usage.fastRequests} / standard ${usage.requests - usage.fastRequests}`;
}

/** Recorded shares exclude missing-speed tokens, even when their estimate uses a faster tier. */
export function describeRecordedSpeed(
  usage: ModelUsage,
  missingTokens: number,
  assumedTier: ServiceTier = 'standard'
) {
  if (!missingTokens) return describeSpeed(usage);
  const tokens = tokensByTier([usage]);
  tokens[assumedTier] = Math.max(0, tokens[assumedTier] - missingTokens);
  return TIERS_BY_SPEED.filter(tier => tokens[tier] > 0)
    .sort((a, b) => tokens[b] - tokens[a])
    .map(tier => `${TIER_LABELS[tier].toLowerCase()} ${formatShare(tokens[tier] / usage.tokens)}`)
    .join(' / ');
}

export function describeUnknownSpeed(missingTokens: number, totalTokens: number) {
  return `${formatTokens(missingTokens)} tokens (${formatShare(missingTokens / totalTokens)})`;
}

/** "14 req · fast 3 / standard 11 · high" — only the parts the provider reported. */
export function describeModelUsage(usage: ModelUsage, speed = describeSpeed(usage)) {
  const parts: string[] = [];
  if (usage.requests) parts.push(`${usage.requests} req`);
  if (speed) parts.push(speed);
  const efforts = [...EFFORT_LEVELS].reverse().filter(level => usage.effort[level] > 0);
  if (efforts.length === 1) parts.push(efforts[0]);
  else if (efforts.length > 1) {
    parts.push(efforts.map(level => `${level} ${usage.effort[level]}`).join(' / '));
  }
  return parts.join(' · ');
}

/** Tokens by tier; standard is what the tier parts leave of each model's totals. */
function tokensByTier(usages: ModelUsage[]) {
  const tokens: Record<ServiceTier, number> = {
    ultrafast: 0,
    priority: 0,
    fast: 0,
    flex: 0,
    standard: 0,
  };
  for (const usage of usages) {
    let tiered = 0;
    for (const tier of EXTRA_TIERS) {
      tokens[tier] += usage.tierTokens[tier];
      tiered += usage.tierTokens[tier];
    }
    tokens.standard += Math.max(0, usage.tokens - tiered);
  }
  return tokens;
}

/** The tiers that carried tokens, biggest share first; a tie keeps the faster tier first. */
function tierShares(tokens: Record<ServiceTier, number>) {
  const total = TIERS_BY_SPEED.reduce((sum, tier) => sum + tokens[tier], 0);
  return TIERS_BY_SPEED.filter(tier => tokens[tier] > 0)
    .map(tier => ({ tier, share: tokens[tier] / total }))
    .sort((a, b) => b.share - a.share);
}

const formatShare = (share: number) => (share < 0.01 ? '<1%' : `${Math.round(share * 100)}%`);

export interface ServiceTierUsage {
  /** The fastest tier used. */
  tier: ExtraTier;
  /**
   * Each tier's share of the tokens, biggest first. Empty when the shares
   * would understate fast: no token ran in `tier`, or a period counted fast
   * requests without fast tokens (Claude uploads from before tier parts), whose
   * fast tokens the other periods' parts would leave counted as standard.
   */
  shares: Array<{ tier: ServiceTier; share: number }>;
}

/** A period's model counted fast requests but not the tokens they ran. */
const countsFastRequestsOnly = (periods: AgentPeriod[]) =>
  periods.some(period =>
    Object.entries(period.tokenUsage).some(
      ([model, counts]) =>
        !PSEUDO_MODELS.has(model) &&
        (counts.fast_requests ?? 0) > 0 &&
        !tokenCounters(counts, 'fast_')
    )
  );

/**
 * The one service tier to badge for these periods: the fastest used, with
 * flex ranked below standard so it shows only when nothing else ran. Null when
 * the periods ran at standard, or only report no tokens at all.
 */
export function serviceTierUsage(periods: AgentPeriod[]): ServiceTierUsage | null {
  const models = modelUsage(periods);
  const tokens = tokensByTier(models);
  const fastRequested = models.some(usage => usage.fastRequests > 0);
  const tier = TIERS_BY_SPEED.find(
    candidate => tokens[candidate] > 0 || (candidate === 'fast' && fastRequested)
  );
  if (!tier || tier === 'standard') return null;
  const sharesKnown = tokens[tier] > 0 && !(tier === 'fast' && countsFastRequestsOnly(periods));
  return { tier, shares: sharesKnown ? tierShares(tokens) : [] };
}

/**
 * "Ultrafast 72% · Standard 28% of tokens". Rails prices each tier's tokens but
 * returns only the period's total estimate, so the split is by tokens, not cost.
 */
export function describeServiceTier({ shares }: ServiceTierUsage) {
  if (!shares.length) return 'Some requests ran in fast mode (billed at a higher rate)';
  return `${shares.map(({ tier, share }) => `${TIER_LABELS[tier]} ${formatShare(share)}`).join(' · ')} of tokens`;
}

/**
 * Local echo of a successful assignment: the periods of `sessionId` (all of
 * them, or only `segmentIds`) take the new project, everything else is
 * returned untouched so memoised rows don't re-render.
 */
export function applyAssignment(
  periods: AgentPeriod[],
  sessionId: number,
  projectId: number | null,
  segmentIds?: number[]
): AgentPeriod[] {
  const only = segmentIds ? new Set(segmentIds) : null;
  return periods.map(period => {
    if (period.sessionId !== sessionId) return period;
    if (only && !only.has(period.segmentId)) return period;
    return period.projectId === projectId ? period : { ...period, projectId };
  });
}

/**
 * Local echo of a confirmed publish, make-private or assignment. The server
 * derives sharing from the whole session, so its response replaces the sharing
 * state of every loaded period of that session; other sessions are returned
 * untouched.
 */
export function applySharing(periods: AgentPeriod[], response: ApiAgentSession): AgentPeriod[] {
  return periods.map(period =>
    period.sessionId === response.id && period.sessionVisibility !== response.visibility
      ? { ...period, sessionVisibility: response.visibility }
      : period
  );
}

/** The one project every period shares, or null when unassigned or mixed. */
export function sharedProjectId(periods: AgentPeriod[]): number | null {
  const ids = new Set(periods.map(period => period.projectId));
  return ids.size === 1 ? (periods[0]?.projectId ?? null) : null;
}

/**
 * Patch the loaded periods from an assign response, but only the segments the
 * request targeted and only the fields it sent. A late response from a bulk
 * tag edit must not drag back an older project set inline in the meantime,
 * and must not touch periods outside its own selection. Sharing is the
 * exception: it is session-wide (see applySharing). Mutations run in request
 * order, so the response is never older than what is already applied.
 */
export function applyReviewResponse(
  periods: AgentPeriod[],
  response: ApiAgentSession,
  body: {
    title?: string;
    speed_override?: { model: string; tier: 'standard' | 'priority' | 'ultrafast' | null };
    project_id?: number | null;
    label_ids?: number[];
    resource_id?: string | null;
    segment_ids?: number[];
  }
): AgentPeriod[] {
  const fresh = new Map(toAgentPeriods([response]).map(period => [period.segmentId, period]));
  const targets = body.segment_ids ? new Set(body.segment_ids) : null;
  const patchedPeriods = periods.map(period => {
    if (period.sessionId !== response.id) return period;
    if ('project_id' in body) period = { ...period, defaultProjectId: response.default_project_id };
    if (targets && !targets.has(period.segmentId)) return period;
    const next = fresh.get(period.segmentId);
    if (!next) return period;
    const patched = { ...period };
    if ('title' in body) patched.title = next.title;
    if ('project_id' in body) {
      patched.projectId = next.projectId;
      patched.projectAssignmentSource = next.projectAssignmentSource;
      patched.projectSuggestion = next.projectSuggestion;
      patched.projectPredictionState = next.projectPredictionState;
      // A manager may edit only periods on their projects, so a move can change it.
      patched.canEdit = next.canEdit ?? patched.canEdit;
      patched.projectName = next.projectName;
      patched.projectColor = next.projectColor;
    }
    if ('label_ids' in body) patched.labelIds = next.labelIds;
    if ('resource_id' in body) patched.resourceId = next.resourceId;
    if ('speed_override' in body) {
      patched.speedOverrides = next.speedOverrides;
      patched.speedUnknownUsage = next.speedUnknownUsage;
      patched.tokenUsage = next.tokenUsage;
      patched.cost = next.cost;
      patched.costAvailable = next.costAvailable;
      patched.pricingVersion = next.pricingVersion;
    }
    return patched;
  });
  return applySharing(patchedPeriods, response);
}

/**
 * Patch the loaded periods from a session returned after its project
 * suggestions changed (predicted, accepted or undone). Its periods take the
 * project assignment, suggestion and sharing it returned, exactly as a project
 * assignment of the whole session would; summaries, usage and tags stay as loaded.
 */
export function applySuggestionResponse(
  periods: AgentPeriod[],
  response: ApiAgentSession
): AgentPeriod[] {
  // Which fields are patched follows the keys a request sent; the value is not read.
  return applyReviewResponse(periods, response, { project_id: null });
}

/** Recorded days only; each metric preserves missing, partial and zero measurements. */
export function dailyAgentActivity(
  periods: AgentPeriod[],
  metric: 'seconds' | 'tokens' | 'cost' = 'seconds'
) {
  return groupSessionPeriods(periods, 'day', () => UNASSIGNED_PROJECT)
    .reverse()
    .map(group => {
      const measured = group.periods.filter(period => hasAgentMetric(period, metric));
      return {
        day: group.key,
        periods: group.periods.length,
        value: summarizePeriods(measured)[metric],
        available: measured.length > 0,
        partial: measured.length > 0 && measured.length < group.periods.length,
      };
    });
}

/** Apply only the tag changes made in a grouped row, preserving each period's other tags. */
export function agentTagAssignmentUpdates(periods: AgentPeriod[], selectedIds: number[]) {
  const previousIds = new Set(periods.flatMap(period => period.labelIds ?? []));
  const selected = new Set(selectedIds);
  const added = selectedIds.filter(id => !previousIds.has(id));
  const groups = new Map<string, { sessionId: number; labelIds: number[]; segmentIds: number[] }>();
  for (const period of periods) {
    const previous = period.labelIds ?? [];
    const labelIds = [...new Set([...previous.filter(id => selected.has(id)), ...added])].sort(
      (a, b) => a - b
    );
    if (labelIds.length === previous.length && previous.every(id => labelIds.includes(id)))
      continue;
    const key = `${period.sessionId}:${labelIds.join(',')}`;
    const group = groups.get(key) ?? { sessionId: period.sessionId, labelIds, segmentIds: [] };
    group.segmentIds.push(period.segmentId);
    groups.set(key, group);
  }
  return [...groups.values()];
}

/** Tags column width when nothing in view is tagged: the empty "Tag" pill alone. */
export const EMPTY_TAGS_COLUMN_WIDTH = 88;
/** Default tags column width; also the cap, so one long tag name truncates instead of growing it. */
export const MAX_TAGS_COLUMN_WIDTH = 180;
// Pill chrome (padding, icon, gap) plus an average glyph at the pill's font size.
const TAG_PILL_CHROME = 46;
const TAG_GLYPH_WIDTH = 6.5;
const TAG_CELL_PADDING = 16;

/**
 * Width that fits the widest tag pill in view. Each session's pill shows its first tag's
 * name and a "+N" for the rest, so the column only needs room for the longest of those.
 */
export function tagsColumnWidth(periods: AgentPeriod[], tagName: (id: number) => string) {
  const sessions = new Map<number, number[]>();
  for (const period of periods) {
    const ids = sessions.get(period.sessionId) ?? [];
    for (const id of period.labelIds ?? []) if (!ids.includes(id)) ids.push(id);
    sessions.set(period.sessionId, ids);
  }
  let widest = 0;
  for (const ids of sessions.values()) {
    if (!ids.length) continue;
    const extra = ids.length > 1 ? `+${ids.length - 1}`.length * TAG_GLYPH_WIDTH + 8 : 0;
    widest = Math.max(widest, TAG_PILL_CHROME + tagName(ids[0]).length * TAG_GLYPH_WIDTH + extra);
  }
  if (!widest) return EMPTY_TAGS_COLUMN_WIDTH;
  return Math.min(
    MAX_TAGS_COLUMN_WIDTH,
    Math.max(EMPTY_TAGS_COLUMN_WIDTH, Math.ceil(widest + TAG_CELL_PADDING))
  );
}
